<template>
  <div
    :class="[
      $style.chip,
      { [$style.selected]: selected },
      { [$style.lightText]: hasLightBackground },
      { [$style.dashed]: variant === 'dashed' },
    ]"
    :style="chipStyle"
    @click="handleClick"
  >
    <span :class="$style.label">{{ label }}</span>
    <button
      v-if="showClear"
      :class="$style.clearButton"
      @click.stop="handleClear"
      type="button"
    >
      ×
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    color?: string;
    selected?: boolean;
    showClear?: boolean;
    label: string;
    variant?: "default" | "dashed";
  }>(),
  {
    selected: false,
    showClear: false,
    variant: "default",
  }
);

const emit = defineEmits<{
  (e: "click"): void;
  (e: "clear"): void;
}>();

const chipStyle = computed(() => {
  if (props.selected && props.color) {
    const style: Record<string, string> = {
      background: props.color,
      borderColor: props.color,
    };

    // Set dark text color for light backgrounds
    const lightColors = ["var(--task-research)", "var(--task-break)"];
    if (lightColors.includes(props.color)) {
      style.color = "#333";
    }

    return style;
  }
  return {};
});

const hasLightBackground = computed(() => {
  if (!props.selected || !props.color) return false;
  const lightColors = ["var(--task-research)", "var(--task-break)"];
  return lightColors.includes(props.color);
});

function handleClick() {
  emit("click");
}

function handleClear() {
  emit("clear");
}
</script>

<style module>
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  justify-content: center;
  white-space: nowrap;
}

.chip:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.chip.selected {
  border-color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}

.chip.selected:not([style*="background"]) {
  background: rgba(255, 255, 255, 0.3);
}

.label {
  flex: 1;
}

.clearButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0;
  margin-left: 0.25rem;
  transition: color 0.2s ease;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clearButton:hover {
  color: white;
}

/* Adjust clear button color for light backgrounds */
.chip.lightText .clearButton {
  color: rgba(0, 0, 0, 0.7);
}

.chip.lightText .clearButton:hover {
  color: #000;
}

.chip.dashed {
  border: 1px dashed rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
}

.chip.dashed:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
}
</style>
